import { useEffect } from 'react';
import { useCanonicalUrl, useHref } from './router/router';
import { useLandingRedirect } from './router/useLandingRedirect';
import { useScreen } from './router/screens';
import { FeedView } from './screens/feed/view';
import { SearchView } from './screens/search/view';
import { HashtagView } from './screens/hashtag/view';
import { ChannelView } from './screens/channel/view';
import { PlaylistView } from './screens/playlist/view';
import { PlaceholderView } from './screens/placeholder/view';
import { ResolveView } from './screens/resolve/view';
import { LoginView } from './screens/login/view';
import { SettingsView } from './screens/settings/view';
import { GatedView } from './screens/gated/view';
import { SubscriptionsView } from './screens/subscriptions/view';
import { ManageSubscriptionsView } from './screens/channels/view';
import { PlaylistsView } from './screens/playlists/view';
import { HistoryView } from './screens/history/view';
import { HandedOverView, WatchView } from './screens/watch/view';
import { EmbedLandingView, EmbedView } from './screens/embed/view';
import { useEmbedScreen, type EmbedTarget, type EmbedVideoTarget } from './screens/embed/model';
import { PlaybackHost } from './playback/PlaybackHost';
import { QueueActionsHost } from './queue/QueueActionsHost';
import { usePlayback } from './playback/context';
import { AppShell } from './shell/AppShell';
import { ScreenBoundary } from './shell/ScreenBoundary';
import { useShell } from './shell/model';
import { TabImportHost } from './tabs/TabImportHost';
import { useHandedOver } from './tabs/store';

/**
 * Routing. `useScreen()` reads the address bar through core's `parseRoute` — this
 * file only names the component for each arm. Since 2f it also runs §8's self-redirect, which is one
 * effect over one pure function rather than a rule per row (`useCanonicalUrl`).
 *
 * A screen whose model needs a hook gets its own component, because hooks cannot be called
 * conditionally and the `switch` below is exactly that.
 */
function WatchScreen() {
  /*
   * The binding is not made here any more, now that the minimized player exists. It belongs to `PlaybackHost`,
   * one level up and outside the router, because the player has to survive a navigation away from
   * this page — see that file for why the whole hook moves rather than just the `<video>`. The host
   * derives its session from the route during render, so on a watch route this is never `null`; the
   * arm exists because the type says it can be, not because it happens.
   */
  const playback = usePlayback();
  /*
   * …with one exception, which is the other reason this arm is not simply `playback.binding`: a tab
   * that has handed its videos to another one has no session at all (`tabs/`), and this page is where
   * that has to be said. Every other screen just goes quiet.
   */
  const handedOver = useHandedOver();
  if (handedOver) return <HandedOverView />;
  return playback === null ? null : <WatchView binding={playback.binding} />;
}

/**
 * The /embed screen is the watch pipeline minus the page around it, plus whatever its own URL asked
 * for — `screens/embed/model.ts` holds both halves and the reasoning for each. The page is still the
 * same hook, so resume, history mirroring and SponsorBlock behave exactly as on /watch.
 */
function EmbedScreen(target: EmbedTarget) {
  // The root stamp `global.css` keys on to release the scrollbar gutter: nothing on this page
  // scrolls the document, and inside an iframe the reserved gutter is a stripe beside the video.
  useEffect(() => {
    document.documentElement.setAttribute('data-embed', '');
    return () => document.documentElement.removeAttribute('data-embed');
  }, []);

  /*
   * Two screens behind one route arm, and the branch is a component boundary rather than a
   * condition inside one: an address naming a list and no video has no player to build until the
   * playlist has answered, and the hook that builds one cannot be called conditionally.
   */
  if (target.videoId === null) {
    return target.list === null ? null : <EmbedLandingView target={{ ...target, list: target.list }} />;
  }
  return <EmbedPlayerScreen {...target} videoId={target.videoId} />;
}

function EmbedPlayerScreen(target: EmbedVideoTarget) {
  return <EmbedView embed={useEmbedScreen(target)} />;
}

function Routed() {
  // §8's self-redirect, and §8's landing page (3e). Both are effects over the address bar, so they
  // belong to whatever renders a *route* rather than to the app's root.
  useCanonicalUrl();
  useLandingRedirect();
  const screen = useScreen();

  switch (screen.kind) {
    case 'home':
      // `/` is the landing redirect (above), already navigating — there is nothing to draw.
      return null;
    case 'feed':
      return <FeedView />;
    case 'search':
      // Keyed on the query text alone, not on the whole query: changing a *filter* must reuse the
      // mounted screen (so the grid's roving focus and the scroll position survive), while searching
      // for something else is a new page and should start at the top with no menu left open.
      return <SearchView key={screen.search.q} query={screen.search} />;
    case 'hashtag':
      return <HashtagView key={screen.tag} tag={screen.tag} page={screen.page} />;
    case 'channel':
      // Keyed by channel, not by tab: switching tabs must reuse the mounted screen so the header does
      // not flash, while a different channel is a different page and starts at the top. Same reading as
      // Search's key, one level up.
      return (
        <ChannelView
          key={screen.channelId}
          channelId={screen.channelId}
          tab={screen.tab}
          sort={screen.sort}
          search={screen.search}
        />
      );
    case 'playlist':
      // Keyed by id: the pages already loaded belong to *this* playlist, and the fold is keyed on
      // `index` — carrying an accumulator across a navigation would interleave two playlists.
      return <PlaylistView key={screen.playlistId} playlistId={screen.playlistId} />;
    case 'watch':
      // No key and no props: the player's identity moved to `PlaybackHost`, which keys the *session*
      // by video for the reason this line used to give — a different video is a different player —
      // and now also decides that the same video arrived at twice is not.
      return <WatchScreen />;
    case 'embed':
      // The watch page's key, for the watch page's reason — and it is what makes an embed playlist
      // work: an advance navigates to the next entry, and a different video is a different player.
      return (
        <EmbedScreen
          // The landing screen has no video to be keyed by, so it is keyed by the entry it is about
          // to become — and the run then re-keys on each real id, which is what makes a step a fresh
          // player rather than a re-used one.
          key={screen.videoId ?? `list:${screen.list?.playlistId ?? ''}:${String(screen.list?.index ?? 0)}`}
          videoId={screen.videoId}
          startSec={screen.startSec}
          options={screen.options}
          list={screen.list}
        />
      );
    case 'resolve':
      // Keyed by the href, so arriving at a second `/@handle` while the first is still in flight is a
      // fresh screen rather than one whose effect fires with the previous route's target.
      return <ResolveView key={screen.href} route={screen.route} />;
    case 'login':
      // Deliberately *not* keyed on `next`: the grant is read once, on mount, and remounting this
      // screen mid-flow would re-read a callback whose token has already been consumed and stripped.
      return <LoginView next={screen.next} />;
    case 'settings':
      return <SettingsView tab={screen.tab} />;
    case 'gated':
      return (
        <GatedView key={screen.feed} feed={screen.feed} signInHref={screen.signInHref}>
          {/* What the gate protects: the three feeds (3b–3d) and the manage-subscriptions page. */}
          {screen.feed === 'subscriptions' ? (
            <SubscriptionsView />
          ) : screen.feed === 'playlists' ? (
            <PlaylistsView />
          ) : screen.feed === 'channels' ? (
            <ManageSubscriptionsView />
          ) : (
            <HistoryView />
          )}
        </GatedView>
      );
    case 'placeholder':
      return (
        <PlaceholderView
          title={screen.title}
          detail={screen.detail}
          slice={screen.slice}
          homeHref={screen.homeHref}
          jumpOuts={screen.jumpOuts}
        />
      );
  }
}

/**
 * The shell, with the boundary **inside** it: a screen whose render throws takes the whole
 * tree down without one, and at the root that is a blank white page. Wrapped here, the header, the nav
 * and the theme toggle survive, so the reader is one click from somewhere that works rather than
 * reaching for the address bar. The href is what clears it — navigating away from a broken page has to
 * be enough.
 */
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <AppShell binding={useShell()}>
      {/*
       * The playback session sits *outside* the boundary and inside the shell: a screen whose
       * render throws must not take a playing video down with it, and the minimized player is a
       * frame-level surface in the same sense the offline notice is — it belongs to the app, not to
       * whichever page happens to be showing.
       */}
      <PlaybackHost>
        {/*
         * Inside the playback host, because building a queue around the video already playing needs
         * the session it provides — and outside the screen boundary, because the dialog and the
         * queue itself belong to the app rather than to whichever page a press happened on.
         */}
        <QueueActionsHost>
          <ScreenBoundary resetKey={useHref()}>{children}</ScreenBoundary>
        </QueueActionsHost>
        {/*
         * The import's confirmation and the channel every tab listens on. Inside the playback host
         * for `QueueActionsHost`'s reason — a list built around the video already playing needs the
         * session — and outside the screen boundary, because the conversation between tabs must
         * survive a page that throws.
         */}
        <TabImportHost />
      </PlaybackHost>
    </AppShell>
  );
}

export function App() {
  // The embed screen is the one page rendered without the shell (2026-08-02): it lives in an
  // <iframe>, where a header and a nav are furniture in a window. It keeps the error boundary —
  // a screen that throws inside somebody else's page must say so rather than go blank.
  const embed = useScreen().kind === 'embed';
  const href = useHref();

  return embed ? (
    <ScreenBoundary resetKey={href}>
      <Routed />
    </ScreenBoundary>
  ) : (
    <Shell>
      <Routed />
    </Shell>
  );
}
